import React, { useRef, useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SeoManager, getPageSeo } from '../seo';
import '../styles/ClickTestsPage.css'; // Assuming you have a CSS file for styles
import ClickTestExtras from '../components/ClickTestExtras';
import TypingSpeedClassifications from '../components/TypingSpeedClassifications';
import FAQSection from '../components/FAQSection';

const DURATION_LABELS = {
  1: '1s',
  5: '5s',
  15: '15s',
  30: '30s',
  60: '60s',
};

const DURATION_ROUTES = {
  1: '/1s-cps-click-test',
  5: '/5s-cps-click-test',
  15: '/15s-cps-click-test',
  30: '/30s-cps-click-test',
  60: '/60s-cps-click-test',
};

const DURATIONS = [1, 5, 15, 30, 60];

export default function ClickSpeedTestPage({ duration = 5 }) {
  const [testDuration, setTestDuration] = useState(duration);
  const [timeLeft, setTimeLeft] = useState(duration);
  const [clicks, setClicks] = useState(0);
  const clicksRef = useRef(0);
  const [testRunning, setTestRunning] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const timerRef = useRef(null);
  const clickAreaRef = useRef(null);
  const hasNavigatedRef = useRef(false);
  const navigate = useNavigate();
  const location = useLocation();
  const configKey = `${duration}s-cps-click-test`;
  const seo = getPageSeo(configKey);

  // State to prevent flicker before navigation
  const [isNavigating, setIsNavigating] = useState(false);

  // Detect if device is touch
  const isTouchDevice = typeof window !== 'undefined' && (
    'ontouchstart' in window || navigator.maxTouchPoints > 0
  );

  // Update duration when prop changes
  useEffect(() => {
    setTestDuration(duration);
    setTimeLeft(duration);
    setClicks(0);
    setShowResults(false);
    setTestRunning(false);
    setIsNavigating(false); // Reset navigating state
    if (timerRef.current) clearInterval(timerRef.current);
  }, [duration]);

  // Ripple effect
  const handleClickArea = (e) => {
    if (!testRunning) return;
    // Ripple
    const ripple = document.createElement('div');
    ripple.className = 'ripple';
    const rect = clickAreaRef.current.getBoundingClientRect();
    ripple.style.left = `${e.clientX - rect.left}px`;
    ripple.style.top = `${e.clientY - rect.top}px`;
    clickAreaRef.current.appendChild(ripple);
    ripple.addEventListener('animationend', () => ripple.remove());
    // Count
    setClicks(c => {
      clicksRef.current = c + 1;
      return c + 1;
    });
  };

  // Start test with specific duration
  const startClickTest = (newDuration) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setTestDuration(newDuration);
    setTimeLeft(newDuration);
    setClicks(0);
    clicksRef.current = 0;
    setShowResults(false);
    setTestRunning(false);
    setIsNavigating(false); // Reset navigating state
  };

  // Start timer on first click
  const handleStart = () => {
    if (testRunning) return;
    setTestRunning(true);
    setShowResults(false);
    setClicks(1); // First click
    clicksRef.current = 1;
    hasNavigatedRef.current = false;
    setIsNavigating(false); // Ensure navigating state is false at start
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          setTestRunning(false);
          setShowResults(true);
          if (!hasNavigatedRef.current) {
            hasNavigatedRef.current = true;
            const finalClicks = clicksRef.current;
            const finalCps = (finalClicks / testDuration).toFixed(2);
            console.log('Final clicks:', finalClicks, 'Final CPS:', finalCps);
            
            setIsNavigating(true); // Set state to true before navigating

            navigate('/click-test-results', {
              state: {
                cps: finalCps,
                clicks: finalClicks,
                duration: testDuration,
              },
            });
          }
          return 0;
        }
        return t - 1;
      });
    }, 1000);
  };

  // Clean up timer
  useEffect(() => () => timerRef.current && clearInterval(timerRef.current), []);

  return (
    <>
      <SeoManager
        title={seo?.title}
        description={seo?.description}
        keywords={seo?.keywords}
        path={seo?.path}
        structuredData={seo?.structuredData}
      />
      {!isNavigating && (
        <div className="clicktest-container">
          <h1>Welcome to {DURATION_LABELS[testDuration] || testDuration + 's'} Click Speed Test</h1>
          <p>Select a duration and click the area as fast as you can!</p>
          <div className="controls">
            {DURATIONS.map(d => (
              <button
                key={d}
                className={testDuration === d ? 'active' : ''}
                onClick={() => {
                  if (location.pathname !== DURATION_ROUTES[d]) {
                    navigate(DURATION_ROUTES[d]);
                  }
                }}
                disabled={testRunning}
              >
                {DURATION_LABELS[d]}
              </button>
            ))}
          </div>
          <div
            className={`click-area${testRunning ? ' running' : ''}`}
            ref={clickAreaRef}
            onClick={testRunning ? handleClickArea : handleStart}
            style={{ cursor: testRunning || !showResults ? 'pointer' : 'default' }}
            tabIndex={0}
            aria-label="Click area for speed test"
          >
            <p id="clickText">{!testRunning && !showResults ? (isTouchDevice ? 'Tap to Start!' : 'Click to Start!') : testRunning ? (isTouchDevice ? 'Tap!' : 'Click!') : ''}</p>
          </div>
          <div className="metrics">
            <div>Time: <span>{timeLeft}</span>s</div>
            <div>Clicks: <span>{clicks}</span></div>
          </div>
          {timeLeft === 0 && !isNavigating && (
            <button className="try-again-btn" onClick={() => startClickTest(testDuration)} style={{marginTop: 24}}>Try Again</button>
          )}
        </div>
      )}
      
      {!isNavigating && <ClickTestExtras duration={testDuration} />}
      {!isNavigating && <TypingSpeedClassifications mode="click" />}
      {!isNavigating && <FAQSection mode="click" />}
    </>
  );
} 